import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Flex, Input, Box, InputProps, Text, FlexProps,
} from '@chakra-ui/react';
import { useFormState } from 'react-hook-form';
import AsyncSelect from 'react-select/async';

import { Icon } from 'components/icon';
import { inputStyle } from 'constants/inputStyles';
import { handleObjectString } from 'utils/parsers';

import HelperMessage from './helper';
import Label from './label';

interface MySelectProps extends InputProps {
    label?:string
    watch?: any
    helper?: string
    viewProps?: { [key: string]: any }
    controlProps?: FlexProps
    defaultOptions?: GenericObject & { label:string }[]
    loadOptions?: (_value: string, _callback: (_:any[]) => void) => void
    isLoading?: boolean
    isClearable?: boolean
    disabledOptions?: GenericObject & { label:string }[]
  }

export const BaseSelect = forwardRef(({
  isDisabled,
  isRequired,
  loadOptions,
  value,
  onChange,
  name,
  defaultOptions,
  placeholder = 'Buscar...',
  controlProps = {},
  isLoading,
  disabledOptions,
  isClearable = true,
  ...rest
}:MySelectProps, ref) => (
  <AsyncSelect
    isLoading={isLoading}
    isDisabled={isDisabled}
    placeholder={placeholder}
    loadOptions={loadOptions}
    defaultOptions={defaultOptions}
    noOptionsMessage={() => 'Nenhum resultado encontrado'}
    openMenuOnClick
    ref={ref as any}
    value={value as any}
    onChange={onChange}
    isClearable={isClearable}
    styles={{
      menu: (base, _props) => ({ ...base, zIndex: 9999 }),
    }}
    name={name}
    components={{
      IndicatorSeparator: () => null,
      LoadingMessage: () => (
        <Flex
          h={10}
          alignItems="center">
          <Text
            px={16}
            variant="smallSemiBold"
            color="gray.400">
            Carregando...
          </Text>
        </Flex>
      ),
      DropdownIndicator: () => <SelectIcon />,
      Option: useCallback(({
        children, innerProps, isFocused, data,
      }) => {
        const props = useMemo(() => {
          const disabled = disabledOptions?.find(({ label }) => label === children);
          let bg = 'white.default';
          let { onClick } = innerProps;
          let prefix = null;

          if (isFocused) {
            bg = 'secondary.500';
          }

          if (disabled) {
            bg = 'gray.200';
            onClick = () => {};
            prefix = (
              <Icon
                name="ban"
                color="gray.500"
              />
            );
          }

          return {
            bg, onClick, prefix,
          };
        }, [disabledOptions, innerProps, isFocused, children]);

        return (
          <Flex
            zIndex={1000}
            h={10}
            p={2}
            alignItems="center"
            {...innerProps}
            {...props}>
            <Text
              hidden={!props.prefix}
              mr={1}
              as="span">
              {props.prefix}
            </Text>
            <Text
              variant="mediumRegular"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis">
              {children}
              <Text
                ml={1}
                variant="xSmallBold"
                as="span">
                {data?.company?.name && `(${data?.company?.name})`}
              </Text>
            </Text>
          </Flex>
        );
      }, [disabledOptions]),
      Control: ({ children, innerProps }) => (
        <Flex
          zIndex={1000}
          {...innerProps}
          as={Box}
          border="1px solid"
          _focusWithin={{
            boxShadow: 's1',
            borderColor: 'primary.default',
          }}
          align="center"
          justify="center"
          borderRadius={4}
          borderColor="gray.400"
          h={10}
          bg="white.default"
          {...controlProps}>
          {children}
        </Flex>
      ),
      Input: ({
        innerRef,
        ...props
      }) => (
        <Input
          zIndex={1000}
          ref={innerRef as any}
          _focus={null}
          isRequired={isRequired}
          border="0"
          h="100%"
          name={name}
          status="status"
          {...rest}
          {...props}
          isDisabled={isDisabled}
        />
      ),
    }}
  />
));

function SelectIcon() {
  return (
    <Flex
      h="100%"
      w="fit-content"
      borderLeft="1px"
      flexDir="column"
      borderColor="gray.400"
      alignItems="center"
      justifyContent="center">
      <Icon
        px={6}
        color="gray.400"
        name="sort"
        size="md"
      />
    </Flex>
  );
}

const Select = forwardRef(({
  label,
  isDisabled,
  isRequired,
  helper,
  viewProps = { },
  name,
  id = 'id',
  loadOptions,
  defaultOptions: options,
  watch,
  ...rest
}: MySelectProps, ref: any) => {
  const [defaultOptions, setDefaultOptions] = useState(options);
  const [isLoading, setIsLoading] = useState(false);
  const { errors } = useFormState({
    name,
  });
  const error = handleObjectString(errors, `${name}.${id}`) ?? handleObjectString(errors, name);
  const status = useMemo(() => (error ? 'error' : 'default'), [error]);
  const { helperColor } = useMemo(() => inputStyle[isDisabled ? 'disabled' : status], [isDisabled, status]);

  useEffect(() => {
    if (loadOptions) {
      setIsLoading(true);
      loadOptions(null, (data) => {
        setDefaultOptions(data);
        setIsLoading(false);
      });
      setTimeout(() => setIsLoading(false), 1000);
    }
  }, [watch]);
  return (
    <Flex
      alignSelf="flex-start"
      width="100%"
      flexDir="column"
      {...viewProps}>
      <Label
        isDisabled={isDisabled}
        label={label}
        isRequired={isRequired}
      />
      <BaseSelect
        ref={ref}
        isLoading={isLoading}
        isDisabled={isDisabled || isLoading}
        isRequired={isRequired}
        name={name}
        loadOptions={loadOptions}
        defaultOptions={defaultOptions}
        {...rest}
      />
      <HelperMessage
        helper={error?.message ?? helper}
        color={helperColor}
      />
    </Flex>
  );
});

export default Select;
